import { Id, IsoDateTime, defineAction, defineEvent, z } from '@mkcore/shared';
import { AUTHENTICATED, definePermissions } from '../permissions';

// Chat identities (docs/04-domeinen/platform.md, Chat-identiteit; CO-1292): every person who chats with MKCore has a
// Hermes profile of their own, with its own bot that only they may use. The profile sends its name in the header
// X-Hermes-Profile on every MCP call (identity_header in Hermes, not the model). Linked with a code that person made,
// a call from the profile runs on behalf of that person: with what the chat agent and the person both may do.

export const chatPermissions = definePermissions('chat', {
  link: 'Link a chat identity to a user with the code that user made: for the chat agent',
});
const C = chatPermissions.keys;

export const CHAT_PLATFORMS = ['telegram', 'teams'] as const;
export const CHAT_CODE_MINUTES = 10;
/** The header Hermes sends with the profile name (identity_header, value_from: profile). */
export const HERMES_PROFILE_HEADER = 'x-hermes-profile';

export const ChatIdentity = z
  .object({
    id: Id,
    userId: Id,
    userName: z.string(),
    platform: z.enum(CHAT_PLATFORMS).describe('The channel of the profile'),
    hermesProfile: z.string().describe('The Hermes profile of this chat'),
    displayName: z.string().nullable(),
    linkedAt: IsoDateTime,
    revokedAt: IsoDateTime.nullable(),
    version: z.number().int(),
  })
  .openapi('ChatIdentity');

export const listChatIdentities = defineAction({
  name: 'admin.chatIdentity.list',
  method: 'get',
  path: '/admin/chat-identities',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: z.object({ items: z.array(ChatIdentity) }),
  summary: 'Your linked chat identities; an admin sees everyone (CO-1292)',
});

export const createChatLinkCode = defineAction({
  name: 'admin.chatIdentity.createCode',
  method: 'post',
  path: '/admin/chat-identities/codes',
  permission: AUTHENTICATED,
  input: z.object({}).strict(),
  output: z.object({ code: z.string().describe('Shown once; send it to the chat bot'), expiresAt: IsoDateTime }),
  summary: 'A code to link a chat identity to yourself, valid 10 minutes and once; a new code replaces the old (CO-1292)',
  // A person makes a code in the web app; an agent never links a chat to someone.
  restOnly: true,
});

export const linkChatIdentity = defineAction({
  name: 'admin.chatIdentity.link',
  method: 'post',
  path: '/admin/chat-identities',
  permission: C.link,
  input: z
    .object({
      code: z.string().trim().min(4).max(20),
      platform: z.enum(CHAT_PLATFORMS).describe('The channel of your profile: telegram or teams'),
      displayName: z.string().trim().max(200).nullable().optional().describe('The name of the sender in the chat, for display'),
    })
    .strict(),
  output: ChatIdentity,
  summary: 'Link the Hermes profile of this chat to the user who made the code (CO-1292)',
  description:
    'The profile comes from the header X-Hermes-Profile that Hermes sends, never from the input. chat-profile-missing without it; chat-code-invalid when the code is wrong, used or expired; chat-identity-taken when the profile is linked to someone else.',
});

export const revokeChatIdentity = defineAction({
  name: 'admin.chatIdentity.revoke',
  method: 'post',
  path: '/admin/chat-identities/{id}/revoke',
  permission: AUTHENTICATED,
  input: z.object({ id: Id }).strict(),
  output: ChatIdentity,
  successStatus: 200,
  summary: 'Unlink a chat identity: your own, or as an admin anyone’s (CO-1292)',
});

export const chatIdentityLinked = defineEvent({
  type: 'platform.chatIdentity.linked',
  version: 1,
  payload: z.object({ chatIdentityId: Id, userId: Id, platform: z.enum(CHAT_PLATFORMS), displayName: z.string().nullable() }),
});

export const chatIdentityEvents = [chatIdentityLinked];
